import React from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Grid
} from '@mui/material';
import {
  PhotoLibrary as PhotoLibraryIcon,
  DateRange as DateIcon,
  LocationOn as LocationIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { getPublicGalleryImageUrl } from '../../utils/galleryImageUtils';

const GalleryCard = ({ 
  gallery, 
  onClick, 
  variant = 'default', // 'default', 'compact', 'featured', 'collage'
  showImageCount = true,
  showDate = true,
  showLocation = true
}) => {
  const getCategoryColor = (category) => {
    const colors = {
      FARM_LIFE: 'primary',
      CROPS: 'success',
      HARVEST: 'warning',
      ACTIVITIES: 'info',
      EVENTS: 'secondary',
      NATURE: 'success',
      INFRASTRUCTURE: 'default',
      PEOPLE: 'primary',
      SEASONS: 'info',
      ORGANIC_PROCESS: 'success'
    };
    return colors[category] || 'default';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      FARM_LIFE: 'Farm Life',
      CROPS: 'Crops',
      HARVEST: 'Harvest',
      ACTIVITIES: 'Activities',
      EVENTS: 'Events',
      NATURE: 'Nature',
      INFRASTRUCTURE: 'Infrastructure',
      PEOPLE: 'People',
      SEASONS: 'Seasons',
      ORGANIC_PROCESS: 'Organic Process'
    };
    return labels[category] || category;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const imageCount = gallery.images?.items?.length || 0;
  const firstThreeImages = gallery.images?.items?.slice(0, 3) || [];

  if (variant === 'compact') {
    return (
      <Card 
        sx={{ 
          cursor: 'pointer',
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-2px)',
            transition: 'all 0.3s ease-in-out'
          }
        }}
        onClick={onClick}
      >
        <Box sx={{ display: 'flex', height: 120 }}>
          <CardMedia
            component="div"
            sx={{ 
              width: 120, 
              height: 120,
              bgcolor: '#f5f5f5',
              backgroundImage: gallery.coverImage ? `url(${getPublicGalleryImageUrl(gallery.coverImage)})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {!gallery.coverImage && <PhotoLibraryIcon sx={{ fontSize: 32, color: 'text.secondary' }} />}
          </CardMedia>
          
          <CardContent sx={{ flex: 1, p: 2 }}>
            <Typography variant="subtitle1" noWrap gutterBottom>
              {gallery.title}
            </Typography>
            
            <Chip
              label={getCategoryLabel(gallery.category)}
              color={getCategoryColor(gallery.category)}
              size="small"
              sx={{ mb: 1 }}
            />
            
            {showImageCount && (
              <Typography variant="caption" display="block" color="text.secondary">
                {imageCount} {imageCount === 1 ? 'photo' : 'photos'}
              </Typography>
            )}
            
            {showDate && gallery.date && (
              <Typography variant="caption" display="block" color="text.secondary">
                {formatDate(gallery.date)}
              </Typography>
            )}
          </CardContent>
        </Box>
      </Card>
    );
  }

  if (variant === 'featured') {
    return (
      <Card 
        sx={{ 
          height: '100%',
          cursor: 'pointer',
          position: 'relative',
          '&:hover': {
            boxShadow: 6,
            transform: 'scale(1.02)',
            transition: 'all 0.3s ease-in-out'
          }
        }}
        onClick={onClick}
      >
        <CardMedia
          component="div"
          height={300}
          sx={{
            bgcolor: '#f5f5f5',
            backgroundImage: gallery.coverImage ? `url(${getPublicGalleryImageUrl(gallery.coverImage)})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          {!gallery.coverImage && <PhotoLibraryIcon sx={{ fontSize: 64, color: 'text.secondary' }} />}
          
          {/* Image count overlay */}
          {showImageCount && imageCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <PhotoLibraryIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption">{imageCount}</Typography>
            </Box>
          )}
        </CardMedia>

        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" component="h3" gutterBottom>
              {gallery.title}
            </Typography>
            <IconButton size="small" color="primary">
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Chip
            label={getCategoryLabel(gallery.category)}
            color={getCategoryColor(gallery.category)}
            size="small"
            sx={{ mb: 1 }}
          />

          {gallery.description && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {gallery.description}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            {showDate && gallery.date && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DateIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {formatDate(gallery.date)}
                </Typography>
              </Box>
            )}
            
            {showLocation && gallery.location && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {gallery.location}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (variant === 'collage') {
    return (
      <Card 
        sx={{ 
          height: '100%',
          cursor: 'pointer',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: 6,
            transform: 'translateY(-4px)',
            transition: 'all 0.3s ease-in-out'
          }
        }}
        onClick={onClick}
      >
        {firstThreeImages.length >= 3 ? (
          <Box sx={{ position: 'relative', height: 250 }}>
            {/* Main image (first image) */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '65%',
                height: '100%',
                backgroundImage: `url(${getPublicGalleryImageUrl(firstThreeImages[0]?.thumbnailUrl)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            />
            
            {/* Second image (top right) */}
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                width: '35%',
                height: '50%',
                backgroundImage: `url(${getPublicGalleryImageUrl(firstThreeImages[1]?.thumbnailUrl)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                borderLeft: '2px solid white'
              }}
            />
            
            {/* Third image (bottom right) */}
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: '35%',
                height: '50%',
                backgroundImage: `url(${getPublicGalleryImageUrl(firstThreeImages[2]?.thumbnailUrl)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                borderLeft: '2px solid white',
                borderTop: '2px solid white'
              }}
            />
            
            {/* Image count overlay */}
            {showImageCount && imageCount > 3 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <PhotoLibraryIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption">+{imageCount - 3}</Typography>
              </Box>
            )}
          </Box>
        ) : (
          // Fallback for galleries with less than 3 images
          <CardMedia
            component="div"
            height={250}
            sx={{
              bgcolor: '#f5f5f5',
              backgroundImage: gallery.coverImage ? `url(${getPublicGalleryImageUrl(gallery.coverImage)})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {!gallery.coverImage && <PhotoLibraryIcon sx={{ fontSize: 48, color: 'text.secondary' }} />}
          </CardMedia>
        )}

        <CardContent sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
              {gallery.title}
            </Typography>
            <IconButton size="small" color="primary">
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Chip
            label={getCategoryLabel(gallery.category)}
            color={getCategoryColor(gallery.category)}
            size="small"
            sx={{ mb: 1 }}
          />

          {gallery.description && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {gallery.description}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            {showImageCount && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhotoLibraryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {imageCount} photos
                </Typography>
              </Box>
            )}
            
            {showDate && gallery.date && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DateIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {formatDate(gallery.date)}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Default variant with preview of first 3 images
  return (
    <Card 
      sx={{ 
        height: '100%',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease-in-out'
        }
      }}
      onClick={onClick}
    >
      {/* Main cover image or first image */}
      <CardMedia
        component="div"
        height={200}
        sx={{
          bgcolor: '#f5f5f5',
          backgroundImage: gallery.coverImage ? `url(${getPublicGalleryImageUrl(gallery.coverImage)})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
      >
        {!gallery.coverImage && <PhotoLibraryIcon sx={{ fontSize: 48, color: 'text.secondary' }} />}
        
        {/* Image count badge */}
        {showImageCount && imageCount > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
          >
            <PhotoLibraryIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">{imageCount}</Typography>
          </Box>
        )}
      </CardMedia>

      {/* Preview of first 3 images */}
      {firstThreeImages.length > 1 && (
        <Box sx={{ p: 1 }}>
          <Grid container spacing={0.5}>
            {firstThreeImages.map((image, index) => (
              <Grid item xs={4} key={image.id}>
                <Box
                  sx={{
                    aspectRatio: '1',
                    bgcolor: '#f5f5f5',
                    backgroundImage: `url(${getPublicGalleryImageUrl(image.thumbnailUrl)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: 0.5,
                    position: 'relative'
                  }}
                >
                  {index === 2 && imageCount > 3 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 0.5
                      }}
                    >
                      <Typography variant="body2" color="white" fontWeight="bold">
                        +{imageCount - 3}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <CardContent>
        <Typography variant="h6" component="h3" noWrap gutterBottom>
          {gallery.title}
        </Typography>

        <Chip
          label={getCategoryLabel(gallery.category)}
          color={getCategoryColor(gallery.category)}
          size="small"
          sx={{ mb: 1 }}
        />

        {gallery.description && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}
          >
            {gallery.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Box>
            {showDate && gallery.date && (
              <Typography variant="caption" color="text.secondary" display="block">
                {formatDate(gallery.date)}
              </Typography>
            )}
            {showLocation && gallery.location && (
              <Typography variant="caption" color="text.secondary" display="block">
                📍 {gallery.location}
              </Typography>
            )}
          </Box>
          
          <IconButton size="small" color="primary">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

export default GalleryCard;